import React from 'react';
import { PropTypes } from '../../../utils/propTypes';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from '../Empty';
import { Spinner } from '../Spinner';
import useLoadingVisibility from './useLoadingVisibility';
import styles from './LoadingState.module.css';

/**
 * LoadingState — THE app-wide loading indicator. Codifies the
 * Empty + Spinner composition (established by the residual-tara-v2
 * phases) so every page/panel loading state shares one look:
 * a muted icon box housing the shared Spinner, a title, and an
 * optional muted description.
 *
 *   <LoadingState
 *     title="Loading project…"      // default "Loading…"
 *     description="…"               // optional second line
 *     variant="inline"              // 'page' | 'inline' | 'overlay'
 *     delay={200}                   // ms before anything renders; 0 = immediate
 *   >
 *     {progressLine}                // optional — rendered in <EmptyContent>
 *   </LoadingState>
 *
 * Variants:
 *   page    — fills + vertically centers in the host pane (flex column)
 *   inline  — plain flow, for panels / modal bodies / dropdowns
 *   overlay — absolute inset-0 wash over existing content (host needs
 *             position: relative)
 *
 * Anti-flicker: renders nothing for the first `delay` ms so sub-delay
 * loads never flash a spinner. Gates that control unmounting and need
 * the companion min-visible rule use `useLoadingVisibility` directly
 * and pass `delay={0}` here.
 */
const VARIANT_CLASS = {
  page: 'page',
  inline: 'inline',
  overlay: 'overlay',
};

const LoadingState = ({
  title = 'Loading…',
  description = null,
  variant = 'inline',
  delay = 200,
  className = '',
  children = null,
}) => {
  const visible = useLoadingVisibility(true, { delay });
  if (!visible) return null;

  const variantClass = styles[VARIANT_CLASS[variant] ?? 'inline'];
  return (
    <div className={`${styles.root} ${variantClass} ${className}`.trim()} data-slot="loading-state">
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Spinner size="lg" />
          </EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          {description && <EmptyDescription>{description}</EmptyDescription>}
        </EmptyHeader>
        {children && <EmptyContent>{children}</EmptyContent>}
      </Empty>
    </div>
  );
};

LoadingState.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  variant: PropTypes.oneOf(['page', 'inline', 'overlay']),
  delay: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default LoadingState;
export { LoadingState };
